import {
  utils,
  LitElement,
  html,
  broadcastReceiver,
  unsafeHTML,
  Task,
  paths,
  t
} from '@hh/global.js'

class HHCustomizerOptions extends LitElement {
  static properties = {
    id: {
      type: Number,
    },
    formId: {
      type: String,
      attribute: 'form-id',
    },
    _componentSkus: {
      type: String,
    },
    _uploadedFiles: {
      type: String,
    },
    productType: {
      type: String,
      attribute: 'product-type',
    },
  }

  constructor() {
    super()
    this.options = []
    this.hasCustomization = false
    this._uploadedFiles = ''
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener(
      'uploaded-files:changed',
      this.onUploadedFilesChange.bind(this)
    )
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  get upchargePrice() {
    return this.productType === 'Medal' ? window.customizer.upcharge.price : 0
  }

  _customizerTask = new Task(this, {
    task: async ([id], { signal }) => {
      const response = await fetch(
        `https://${window.customizer.host}/api/public/product_configurations/${id}.json`,
        { signal }
      )

      if (!response.ok) {
        this.dispatchEvent(
          new CustomEvent('customization-payload:loaded', {
            bubbles: true,
          })
        )

        throw new Error(response.status)
      }

      const results = await response.json()

      this.hasCustomization = results && (Object.keys(results) || []).length > 0

      window.customizer['payload'] = results

      this.options = results?.customization_options || []
      this._componentSkus = this.options
        .map(option => option.values[0].sku)
        .join(',')

      this.dispatchEvent(
        new CustomEvent('customization-payload:loaded', {
          bubbles: true,
        })
      )

      return results
    },
    args: () => [this.id],
  })

  handleChange(e) {
    let skuMap = this._componentSkus.split(',')
    const index = this.options.findIndex(
      option => `properties[${option.title}]` === e.target.name
    )
    skuMap[index] = e.detail.option.sku

    this._componentSkus = skuMap.join(',')

    this.dispatchEvent(
      new CustomEvent('changeSelectedCustomization', {
        bubbles: true,
        composed: true,
        detail: {
          option: e.detail.option
        },
      })
    )
  }

  onPersonalizeOptionChange(e) {
    this.dispatchEvent(
      new CustomEvent('changePersonalizeOption', {
        bubbles: true,
        composed: true,
        detail: {
          value: e.target.value,
        },
      })
    )
  }

  onUploadedFilesChange(e) {
    const { files } = e.detail
    this._uploadedFiles = files.map(file => file.url).join(',')
  }

  createRenderRoot() {
    return this
  }

  render() {
    return this._customizerTask.render({
      pending: () =>
        html`
          <p>${t('products.personalize.loading')}</p>
        `,
      complete: results =>
        this.hasCustomization
          ? html`
              ${!!this._uploadedFiles
                ? html`
                    <input
                      type="hidden"
                      name="properties[_uploaded_file]"
                      value="${this._uploadedFiles}"
                      form="${this.formId}"
                    />
                  `
                : ''}
              ${!!this._componentSkus
                ? html`
                    <input
                      type="hidden"
                      name="properties[_componentSkus]"
                      value="${this._componentSkus}"
                      form="${this.formId}"
                    />
                  `
                : ''}

              <div class="flex fdc gap-md">
                <div
                  class="customizer-options flex fdc gap-sm ${this.options
                    .length > 0
                    ? ''
                    : 'hidden'}"
                >
                  ${this.options.map(option => {
                    const dropdownOptions = option.values.map(value => ({
                      key: option.title,
                      label: value.title,
                      value: value.title,
                      sku: value.sku,
                      image: value.image_url,
                    }))

                    return html`
                      <hh-custom-select
                        options="${JSON.stringify(dropdownOptions)}"
                        name="properties[${option.title}]"
                        placeholder="${t('products.personalize.provide_search_terms')}"
                        @change="${this.handleChange}"
                        active="${dropdownOptions[0].value}"
                      >
                        <span slot="title">${option.title}</span>
                        <span slot="icon">${unsafeHTML(paths.caret)}</span>
                        <input
                          slot="input"
                          type="hidden"
                          name="properties[${option.title}]"
                          value="${dropdownOptions[0].value}"
                          form="${this.formId}"
                        />
                      </hh-custom-select>
                    `
                  })}
                </div>
                <div class="flex fdc gap-xs">
                  <div class="utility">
                    ${t('products.personalize.select_options')}
                  </div>
                  <div class="flex gap-md pv05">
                    <hh-radio-wrapper
                      @change="${this.onPersonalizeOptionChange}"
                    >
                      <input
                        type="radio"
                        slot="input"
                        id="personalize-yes"
                        name="personalize"
                        value="${t('products.personalize.yes')}"
                        form="${this.formId}"
                        checked
                      />
                      <label slot="label" for="personalize-yes"
                        >${t('products.personalize.yes')}
                        ${this.upchargePrice > 0
                          ? `+${utils.formatPrice(this.upchargePrice)}`
                          : '(Free)'}
                      </label>
                    </hh-radio-wrapper>

                    <hh-radio-wrapper
                      @change="${this.onPersonalizeOptionChange}"
                    >
                      <input
                        type="radio"
                        slot="input"
                        id="personalize-no"
                        name="personalize"
                        form="${this.formId}"
                        value="${t('products.personalize.no')}"
                      />
                      <label slot="label" for="personalize-no">${t('products.personalize.no')}</label>
                    </hh-radio-wrapper>
                  </div>
                </div>
              </div>
            `
          : '',
      error: e => html``,
    })
  }
}

customElements.define('hh-customizer-options', HHCustomizerOptions)
